import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Section } from '../ui/Section';
import { useServices } from '../../hooks/useServices';
export function ServicesSection() {
  const {
    services
  } = useServices();
  // Track which full-width card we're on (to alternate layout direction)
  let fullWidthCount = 0;
  return <Section id="services" background="white" className="!pt-10 md:!pt-16" data-id="element-232">
      <div className="max-w-3xl mb-14" data-id="element-233">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-3xl md:text-4xl font-bold font-heading text-[#0C0C0C]" data-id="element-234">
          What we do
        </motion.h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-235">
        {services.map((service, index) => {
        const isFullWidth = index % 3 === 0;
        if (isFullWidth) fullWidthCount++;
        // Alternate full-width cards: odd = text-left/image-right, even = image-left/text-right
        const isReversed = isFullWidth && fullWidthCount % 2 === 0;
        if (isFullWidth) {
          // Full-width card — side by side layout
          return <motion.div key={service.id} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.08
          }} className="md:col-span-2" data-id="element-236">
                <Link to={`/services/${service.slug}`} className="block group" data-id="element-237">
                  <div className={`bg-transparent border-2 border-gray-200 rounded-2xl p-6 md:p-8 flex flex-col ${isReversed ? 'md:flex-row-reverse' : 'md:flex-row'} gap-6 md:gap-8 min-h-[280px] transition-all duration-300 group-hover:bg-[#FF6600] group-hover:border-[#FF6600] group-hover:shadow-lg`} data-id="element-238">
                    {/* Text side */}
                    <div className="flex-1 flex flex-col justify-between" data-id="element-239">
                      <div data-id="element-240">
                        <h3 className="text-2xl md:text-3xl font-bold font-heading text-[#0C0C0C] mb-4 transition-colors duration-300 group-hover:text-white" data-id="element-241">
                          {service.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed text-base transition-colors duration-300 group-hover:text-white/85 line-clamp-4" data-id="element-242">
                          {service.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 mt-6 text-[#FF6600] transition-colors duration-300 group-hover:text-white" data-id="element-243">
                        <span className="text-sm font-medium" data-id="element-244">Read More</span>
                        <div className="w-6 h-6 rounded-full border border-[#FF6600]/50 flex items-center justify-center transition-all duration-300 group-hover:border-white/50 group-hover:bg-white/15" data-id="element-245">
                          <ArrowUpRight className="w-3.5 h-3.5" data-id="element-246" />
                        </div>
                      </div>
                    </div>
                    {/* Image side */}
                    {service.image ? <div className="flex-1 rounded-xl overflow-hidden bg-gray-100 aspect-[3/1]" data-id="element-247">
                        <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-248" />
                      </div> : <div className="flex-1 rounded-xl bg-gray-50 aspect-[3/1] transition-colors duration-300 group-hover:bg-white/10" data-id="element-249" />}
                  </div>
                </Link>
              </motion.div>;
        }
        // Half-width card — stacked layout (text on top, image below)
        return <motion.div key={service.id} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.08
        }} className="md:col-span-1" data-id="element-250">
              <Link to={`/services/${service.slug}`} className="block h-full group" data-id="element-251">
                <div className="bg-transparent border-2 border-gray-200 rounded-2xl p-6 md:p-8 flex flex-col h-full min-h-[420px] transition-all duration-300 group-hover:bg-[#FF6600] group-hover:border-[#FF6600] group-hover:shadow-lg" data-id="element-252">
                  <div className="flex-1" data-id="element-253">
                    <h3 className="text-2xl font-bold font-heading text-[#0C0C0C] mb-4 transition-colors duration-300 group-hover:text-white" data-id="element-254">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-base transition-colors duration-300 group-hover:text-white/85 line-clamp-4" data-id="element-255">
                      {service.description}
                    </p>
                  </div>
                  {/* Image */}
                  {service.image ? <div className="w-full aspect-[3/1] rounded-xl overflow-hidden bg-gray-100 mt-6" data-id="element-256">
                      <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-257" />
                    </div> : <div className="w-full aspect-[3/1] rounded-xl bg-gray-50 mt-6 transition-colors duration-300 group-hover:bg-white/10" data-id="element-258" />}
                  <div className="flex items-center gap-2 mt-5 text-[#FF6600] transition-colors duration-300 group-hover:text-white" data-id="element-259">
                    <span className="text-sm font-medium" data-id="element-260">Read More</span>
                    <div className="w-6 h-6 rounded-full border border-[#FF6600]/50 flex items-center justify-center transition-all duration-300 group-hover:border-white/50 group-hover:bg-white/15" data-id="element-261">
                      <ArrowUpRight className="w-3.5 h-3.5" data-id="element-262" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>;
      })}
      </div>
    </Section>;
}